import React from 'react';
import { Button, message, Modal } from 'antd';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import Seperator from 'components/UploadPatientListModal/imgs/Seperator.svg';
import Done from 'assets/klarityReview/step.svg';
import FirstStep from './components/FirstStep';
import SecondStep from './components/SecondStep';
import ThirdStep from './components/ThirdStep';
import { ReviewData, ThirdStepDataType } from './types';
import { batchSaveReviews, getUploadedReviewCount } from 'api/klarityReview';

type Props = {
    onCannel?: (needRefresh?: boolean) => void;
    onSuccess?: () => void;
    show?: boolean;
};

const UploadReviewsListModal = (props: Props) => {
    const { onCannel, onSuccess, show } = props;
    const [step, setStep] = React.useState(1);
    const [secondStepData, setSecondStepData] = React.useState<ReviewData[] | undefined>(undefined);
    const [thirdStepData, setThirdStepData] = React.useState<ThirdStepDataType | undefined>(undefined);
    const [submiting, setSubmiting] = React.useState(false);
    const [uploadError, setUploadError] = React.useState<string>('');
    const [uploadedCount, setUploadedCount] = React.useState<number>(0);
    const MAX_UPLOAD_LIMIT = 50; // 最大上传条数限制

    // 获取已上传条数
    const fetchUploadedCount = React.useCallback(async () => {
        try {
            const result = await getUploadedReviewCount();
            if (result && !result.error && result.data?.code === 200) {
                setUploadedCount(result.data.data || 0);
            } else {
                setUploadedCount(0);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch uploaded count:', e);
            setUploadedCount(0);
        }
    }, []);

    React.useEffect(() => {
        if (!show) {
            setStep(1);
            setSecondStepData(undefined);
            setThirdStepData(undefined);
            setSubmiting(false);
            setUploadError('');
            setUploadedCount(0);
        } else {
            // 当 Modal 打开时，获取已上传条数
            fetchUploadedCount();
        }
    }, [show, fetchUploadedCount]);

    const handleNext = async () => {
        if (step === 1) {
            setStep(step + 1);
            return;
        }
        if (step === 2) {
            if (!secondStepData || secondStepData.length === 0) {
                message.error('Please upload a CSV file first.');
                return;
            }

            // Process the uploaded reviews data
            const thirdData: ThirdStepDataType = {
                importedAmount: secondStepData.length,
                failedAmount: 0,
                reviews: secondStepData,
            };

            setThirdStepData(thirdData);
            setStep(step + 1);
            return;
        }
        if (step === 3) {
            if (!thirdStepData || thirdStepData.reviews.length === 0) {
                message.error('No reviews to upload.');
                return;
            }

            // 检查上传条数限制
            const remainingQuota = MAX_UPLOAD_LIMIT - uploadedCount;
            if (thirdStepData.reviews.length > remainingQuota) {
                message.error(`You can only upload ${remainingQuota} more review(s). The maximum limit is ${MAX_UPLOAD_LIMIT} reviews, and you have already uploaded ${uploadedCount}.`);
                return;
            }

            setSubmiting(true);
            try {
                // 调用上传评论的 API
                const result = await batchSaveReviews(thirdStepData.reviews);

                // 检查返回结果
                if (result && !result.error && result.data?.code === 200) {
                    message.success('Reviews uploaded successfully');
                    // 更新已上传条数
                    await fetchUploadedCount();
                    onSuccess?.();
                } else {
                    const errorMsg = result?.error || result?.data?.message || 'Upload failed';
                    message.error(errorMsg);
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to upload reviews:', e);
                message.error('Failed to upload reviews. Please try again.');
            } finally {
                setSubmiting(false);
            }
        }
    };

    return (
        <Modal
            title="Upload patient reviews"
            closable
            className={`${commonS.modalFixHeightWrap} ${commonS.modalFixSetedHeightWrap}`}
            width="1200px"
            onCancel={() => {
                setStep(1);
                setSecondStepData(undefined);
                setThirdStepData(undefined);
                setUploadError('');
                onCannel?.();
            }}
            okText={step === 3 ? 'Upload now' : 'Next'}
            open={show}
            footer={
                <div className={s.footer}>
                    {
                        step === 2 || step === 3 ?
                            <Button
                                onClick={() => {
                                    setStep(step - 1);
                                }}
                                loading={submiting}
                            >
                                Back
                            </Button>
                            :
                            <span />
                    }
                    <Button
                        onClick={handleNext}
                        type="primary"
                        loading={submiting}
                        disabled={
                            (step === 2 && (!secondStepData || secondStepData.length === 0 || !!uploadError)) ||
                            (step === 3 && (!thirdStepData || thirdStepData.reviews.length === 0))
                        }
                    >
                        {step === 3 ? 'Upload now' : 'Next'}
                    </Button>
                </div>
            }
            destroyOnClose
        >
            <div className={s.wrap}>
                <div className={s.stepWrap}>
                    <div className={step === 1 ? `${s.stepItem} ${s.stepItemActive}` : s.stepItem}>
                        <div className={s.left}>
                            {
                                step > 1 ? <img src={Done} className={s.icon} alt="done" /> : <span className={s.num}>01</span>
                            }
                        </div>
                        <div className={s.right}>
                            <div className={s.title}>
                                PREPARE
                            </div>
                            <div className={s.desc}>
                                Copy template and enter patient reviews
                            </div>
                        </div>
                    </div>
                    <div className={step === 2 ? `${s.stepItem} ${s.stepItemActive}` : s.stepItem}>
                        <div className={s.spe}>
                            <img src={Seperator} alt="separator" />
                        </div>
                        <div className={s.left}>
                            {
                                step > 2 ? <img src={Done} className={s.icon} alt="done" /> : <span className={s.num}>02</span>
                            }
                        </div>
                        <div className={s.right}>
                            <div className={s.title}>
                                UPLOAD
                            </div>
                            <div className={s.desc}>
                                Upload .csv file
                            </div>
                        </div>
                    </div>
                    <div className={step === 3 ? `${s.stepItem} ${s.stepItemActive}` : s.stepItem}>
                        <div className={s.spe}>
                            <img src={Seperator} alt="separator" />
                        </div>
                        <div className={s.left}>
                            <span className={s.num}>03</span>
                        </div>
                        <div className={s.right}>
                            <div className={s.title}>
                                CONFIRM
                            </div>
                            <div className={s.desc}>
                                Confirm patient reviews
                            </div>
                        </div>
                    </div>
                </div>
                {
                    step === 1 && <FirstStep />
                }
                {
                    step === 2 &&
                    <SecondStep
                        onUpdate={(data) => {
                            setSecondStepData(data);
                            // 如果有数据且没有错误，清除错误状态
                            if (data && data.length > 0) {
                                setUploadError('');
                            }
                        }}
                        onError={(error) => {
                            setUploadError(error);
                            // 如果有错误，清除数据
                            if (error) {
                                setSecondStepData(undefined);
                            }
                        }}
                        error={uploadError}
                        remainingQuota={MAX_UPLOAD_LIMIT - uploadedCount}
                    />
                }
                {
                    step === 3 &&
                    <ThirdStep
                        data={thirdStepData}
                        onUpdate={(data) => {
                            if (thirdStepData) {
                                setThirdStepData({
                                    ...thirdStepData,
                                    reviews: data || [],
                                });
                            }
                        }}
                    />
                }
            </div>
        </Modal>
    );
};

export default UploadReviewsListModal;
